import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { MultiPickList } from './MultiPickList';
import { manualEntriesApi } from '../services/api';
import { toast } from 'sonner';
import { ClipboardCheck, Loader2 } from 'lucide-react';

import { utcToSydLocalInput, sydLocalInputToUtcIso } from '../utils/sydneyTime';

export const toLocalInput = utcToSydLocalInput;

/**
 * Log hours for a shift that was never clocked. Workers submit (pending approval);
 * supervisors/admins are auto-approved. `preset` may carry worker_id, client_ids,
 * clock_in/clock_out (ISO), roster_shift_id.
 */
export function ManualEntryDialog({ open, onOpenChange, isScheduler, workers = [], clients = [], preset, onSaved }) {
  const [form, setForm] = useState({ worker_id: '', client_ids: [], clock_in: '', clock_out: '', break_minutes: 0, reason: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = preset?.clock_in ? new Date(preset.clock_in) : new Date(Date.now() - 4 * 3600 * 1000);
    const end = preset?.clock_out ? new Date(preset.clock_out) : new Date(base.getTime() + 3 * 3600 * 1000);
    setForm({
      worker_id: preset?.worker_id || '',
      client_ids: preset?.client_ids || [],
      clock_in: toLocalInput(base),
      clock_out: toLocalInput(end),
      break_minutes: preset?.break_minutes || 0,
      reason: preset?.reason || '',
    });
  }, [open, preset]);

  const hours = (() => {
    const a = new Date(sydLocalInputToUtcIso(form.clock_in)), b = new Date(sydLocalInputToUtcIso(form.clock_out));
    if (isNaN(a) || isNaN(b) || b <= a) return 0;
    return Math.max((b - a) / 3600000 - (parseInt(form.break_minutes) || 0) / 60, 0).toFixed(2);
  })();

  const submit = async () => {
    if (isScheduler && !form.worker_id) { toast.error('Select a worker'); return; }
    if (!form.reason.trim()) { toast.error('Please give a reason (e.g. forgot to clock in)'); return; }
    if (form.clock_out <= form.clock_in) { toast.error('End must be after start'); return; }
    setSaving(true);
    try {
      const { data } = await manualEntriesApi.create({
        worker_id: form.worker_id || null,
        client_ids: form.client_ids,
        roster_shift_id: preset?.roster_shift_id || null,
        clock_in: sydLocalInputToUtcIso(form.clock_in),
        clock_out: sydLocalInputToUtcIso(form.clock_out),
        break_minutes: parseInt(form.break_minutes) || 0,
        reason: form.reason.trim(),
      });
      toast.success(data.approval_status === 'approved' ? `${data.total_hours}h added to timesheet` : `${data.total_hours}h submitted — awaiting supervisor approval`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save manual entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="manual-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-teal-600" /> Log hours manually</DialogTitle>
          <DialogDescription>
            {isScheduler ? 'Entry is approved immediately and flagged “Manual” on the timesheet.' : 'Your supervisor will be notified to approve these hours before they count toward pay.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isScheduler && (
            <div>
              <Label className="text-sm">Worker</Label>
              <Select value={form.worker_id} onValueChange={(v) => setForm({ ...form, worker_id: v })}>
                <SelectTrigger className="mt-1" data-testid="manual-worker"><SelectValue placeholder="Choose worker" /></SelectTrigger>
                <SelectContent>
                  {workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-sm">Participant(s)</Label>
            <MultiPickList items={clients.map(c => ({ id: c.id, name: c.name }))} selected={form.client_ids}
              onChange={(ids) => setForm({ ...form, client_ids: ids })} placeholder="Search participants…" emptyLabel="Optional — who was supported" testId="manual-clients" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-sm">Start</Label>
              <Input type="datetime-local" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} className="mt-1" data-testid="manual-start" />
            </div>
            <div>
              <Label className="text-sm">End</Label>
              <Input type="datetime-local" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} className="mt-1" data-testid="manual-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <Label className="text-sm">Unpaid break (min)</Label>
              <Input type="number" min={0} value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} className="mt-1" data-testid="manual-break" />
            </div>
            <div className="rounded-lg bg-teal-50 border border-teal-200 px-3 py-2 text-sm text-teal-800" data-testid="manual-hours-preview">
              <span className="font-semibold">{hours}h</span> payable
            </div>
          </div>
          <div>
            <Label className="text-sm">Reason <span className="text-rose-500">*</span></Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1"
              placeholder="e.g. Forgot to clock in — phone battery flat. Shift confirmed with participant." data-testid="manual-reason" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="manual-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : isScheduler ? 'Add to timesheet' : 'Submit for approval'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
